// src/components/layout/TopNav.tsx
// A responsive top navigation bar with logo, navigation links, user menu, and mobile sheet.
// All comments are in English.

import * as React from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, LogOut, User2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  canAccessAutodeskCustomer,
  canAccessHelpdeskDashboard,
  canAccessOpportunityDashboard,
  canAccessOdooCustomers,
  canAccessSalesIntelligence,
  canAccessSalesRankingInsights,
} from "@/auth/access";

type NavItem = { label: string; to: string };

const NAV_ITEMS: NavItem[] = [
  { label: "Sales Ranking & Insights", to: "/odoo/sales-ranking-insights" },
  { label: "Opportunity Dashboard", to: "/opportunity-dashboard" },
  { label: "Sales Intelligence (Autodesk)", to: "/autodesk-usage/sales-intelligence" },
  { label: "Autodesk Customer", to: "/autodesk-usage/corporate" },
  { label: "Multi Companies Active Licenses", to: "/odoo/customer/multi-licenses-overview" },
  { label: "Customers", to: "/odoo/customer" },
  { label: "Helpdesk Dashboard", to: "/odoo/helpdesk-dashboard" },
//  { label: "Autodesk Risk (Companies)", to: "/autodesk-usage/customers" },
//  { label: "Autodesk Products Dashboard", to: "/autodesk-usage/products" },
];

interface TopNavProps {
  userName?: string;
  userGroups?: string[];
  /**
   * Optional logout handler provided by the parent.
   * This should call the backend /auth/logout endpoint
   * so that the server can clear any HttpOnly session cookies.
   */
  onLogout?: () => void | Promise<void>;
}

export const TopNav: React.FC<TopNavProps> = ({
  userName = "User",
  userGroups = [],
  onLogout,
}) => {
  const navItems = React.useMemo(
    () =>
      NAV_ITEMS.filter((item) => {
        if (item.to === "/odoo/sales-ranking-insights") return canAccessSalesRankingInsights(userGroups);
        if (item.to === "/autodesk-usage/sales-intelligence") return canAccessSalesIntelligence(userGroups);
        if (item.to === "/autodesk-usage/corporate") return canAccessAutodeskCustomer(userGroups);
        if (item.to === "/opportunity-dashboard") return canAccessOpportunityDashboard(userGroups);
        if (item.to === "/odoo/helpdesk-dashboard") return canAccessHelpdeskDashboard(userGroups);
        if (item.to === "/odoo/customer" || item.to === "/odoo/customer/multi-licenses-overview") {
          return canAccessOdooCustomers(userGroups);
        }
        return true;
      }),
    [userGroups]
  );

  // Centralized logout click handler used by both desktop and mobile logout buttons.
  const handleLogout = React.useCallback(async () => {
    try {
      // Delegate the full logout flow (backend + redirect) to the parent handler.
      if (onLogout) {
        await Promise.resolve(onLogout());
        return;
      }
    } catch {
      // Ignore errors to avoid blocking logout UX.
    }

    // Fallback: if no onLogout is provided, just navigate to the login page.
    try {
      window.location.replace("/login");
    } catch {
      window.location.href = "/login";
    }
  }, [onLogout]);

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/75">
      <div className="mx-auto max-w-7xl px-4 md:px-6 h-14 flex items-center justify-between gap-3">
        {/* Left: logo and desktop navigation */}
        <div className="flex items-center gap-6">
          <Link to="/" className="font-semibold tracking-tight text-lg">
            <span className="mr-1">License</span>
            <span className="rounded-md bg-foreground px-1.5 py-0.5 text-background text-sm align-middle">
              BI
            </span>
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "px-3 py-1.5 text-sm rounded-md transition-colors",
                    isActive
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Right: user menu and mobile trigger */}
        <div className="flex items-center gap-2">
          {/* Desktop user menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="hidden md:inline-flex gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-[10px]">
                    {initials(userName)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm">{userName}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Account</DropdownMenuLabel>

              {/* Disabled Profile item (no navigation, no 404) */}
              <DropdownMenuItem
                disabled
                className="flex items-center gap-2 opacity-60 cursor-not-allowed"
              >
                <User2 className="h-4 w-4" />
                <span>Profile (coming soon)</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile: hamburger menu with sheet */}
          <Sheet>
            <SheetTrigger asChild>
              <Button size="icon" variant="outline" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px]">
              <SheetHeader>
                <SheetTitle>License BI</SheetTitle>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        "px-3 py-2 rounded-md text-sm transition-colors",
                        isActive
                          ? "bg-accent text-accent-foreground"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent"
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>

              <div className="mt-8 border-t pt-4">
                <div className="text-xs text-muted-foreground mb-2">
                  Signed in as
                </div>
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>{initials(userName)}</AvatarFallback>
                  </Avatar>
                  <div className="text-sm">{userName}</div>
                </div>
                <Button
                  variant="destructive"
                  className="mt-4 w-full"
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

// Utility: generate initials from user's name
function initials(name?: string) {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts[1]?.[0] ?? "";
  return (first + last).toUpperCase() || "U";
}

export default TopNav;
